import { GOAL_FIELD_IDS } from "@components/GoalsSections/helper";
import { GeneralizationCriteriaOptions } from "@components/PDFGenerator/Catalight/catalightUtils";

export interface GoalApprovalGoalData {
  goalName?: string | null;
  status?: string | null;
  masteryCriteria?: string | null;
  baselineData?: string | null;
  baselineDate?: string | null;
  generalizationCriteria?: string | null;
  assessmentToolSource?: string | null;
  rationale?: string | null;
  gasLevel1?: string | null;
  gasLevel3?: string | null;
  gasLevel4?: string | null;
  gasScore?: string | null;
}

export interface GoalApprovalTemplateField {
  fieldIdentifier?: string | null;
}

export interface GoalApprovalContext {
  isGasEnabled: boolean;
  isCatalightTemplate: boolean;
  shouldShowRationale: boolean;
  goalFields: ReadonlyArray<GoalApprovalTemplateField | null | undefined>;
}

export interface GoalApprovalError {
  fieldKey: string;
  message: string;
}

// goalFields is already filtered to enabled fields (useTreatmentPlanTemplate), so a
// field the org's template has disabled is never surfaced to the clinician and must
// not block approval.
export const isGoalFieldEnabled = (goalFields: GoalApprovalContext["goalFields"], fieldIdentifier: string): boolean =>
  goalFields.some((field) => field?.fieldIdentifier === fieldIdentifier);

function getGoalTitleAndStatusErrors(goalData: GoalApprovalGoalData | undefined): GoalApprovalError[] {
  const errors: GoalApprovalError[] = [];
  if (!goalData?.goalName?.trim()) errors.push({ fieldKey: "goalName", message: "Goal Title" });
  if (!goalData?.status?.trim()) errors.push({ fieldKey: "status", message: "Goal status" });
  return errors;
}

function getNonGasApprovalErrors(
  goalData: GoalApprovalGoalData | undefined,
  { isCatalightTemplate, goalFields }: Pick<GoalApprovalContext, "isCatalightTemplate" | "goalFields">,
): GoalApprovalError[] {
  const errors: GoalApprovalError[] = [];
  if (isGoalFieldEnabled(goalFields, GOAL_FIELD_IDS.MASTERY_CRITERIA) && !goalData?.masteryCriteria?.trim()) {
    errors.push({ fieldKey: "masteryCriteria", message: "Mastery Criteria" });
  }
  if (isGoalFieldEnabled(goalFields, GOAL_FIELD_IDS.BASELINE_DATA) && !goalData?.baselineData?.trim()) {
    errors.push({ fieldKey: "baselineData", message: "Baseline Data" });
  }
  if (
    isCatalightTemplate &&
    goalData?.generalizationCriteria === GeneralizationCriteriaOptions.NOT_APPLICABLE &&
    !goalData?.rationale?.trim()
  ) {
    errors.push({ fieldKey: "rationale", message: "Rationale" });
  }
  return errors;
}

// Deliberately not gated by isGoalFieldEnabled: these are GAS levels populated by
// GasPanel's own generation flow rather than the org's optional-field template config,
// so template field presence doesn't apply.
function getGasApprovalErrors(
  goalData: GoalApprovalGoalData | undefined,
  shouldShowRationale: boolean,
): GoalApprovalError[] {
  const requiredFields: Array<{ value: string | null | undefined; fieldKey: string; message: string }> = [
    { value: goalData?.baselineData, fieldKey: "baselineData", message: "Gas level 0" },
    { value: goalData?.masteryCriteria, fieldKey: "masteryCriteria", message: "Gas level 2" },
    { value: goalData?.gasLevel1, fieldKey: "gasLevel1", message: "Gas level 1" },
    { value: goalData?.gasLevel3, fieldKey: "gasLevel3", message: "Gas level 3" },
    { value: goalData?.gasLevel4, fieldKey: "gasLevel4", message: "Gas level 4" },
    { value: goalData?.gasScore, fieldKey: "gasScore", message: "GAS score" },
    { value: goalData?.baselineDate, fieldKey: "baselineDate", message: "Baseline Date" },
    { value: goalData?.assessmentToolSource, fieldKey: "assessmentToolSource", message: "Assessment Tool Source" },
    {
      value: goalData?.generalizationCriteria,
      fieldKey: "generalizationCriteria",
      message: "Generalization Criteria",
    },
  ];

  const errors = requiredFields
    .filter(({ value }) => !value?.trim())
    .map(({ fieldKey, message }) => ({ fieldKey, message }));

  if (shouldShowRationale && !goalData?.rationale?.trim()) {
    errors.push({ fieldKey: "rationale", message: "Rationale" });
  }
  return errors;
}

export function getGoalApprovalErrors(
  goalData: GoalApprovalGoalData | undefined,
  { isGasEnabled, isCatalightTemplate, shouldShowRationale, goalFields }: GoalApprovalContext,
): GoalApprovalError[] {
  const errors = getGoalTitleAndStatusErrors(goalData);
  errors.push(
    ...(isGasEnabled
      ? getGasApprovalErrors(goalData, shouldShowRationale)
      : getNonGasApprovalErrors(goalData, { isCatalightTemplate, goalFields })),
  );
  return errors;
}
